import { Wallet } from 'lucide-react';
import { Link } from 'react-router';

import { Badge } from '@/components/ui/badge';
import { DataLoader } from '@/components/ui/DataLoader';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useFundCostRecognition } from '@/hooks/react-query/useFundCosts';
import {
  FUND_COST_CATEGORY_LABELS,
  type FundCostRecognitionEntry,
} from '@/types/fund-costs';
import {
  FUND_TRANSACTION_TYPE_LABELS,
  type FundTransactionDetail,
  type ReconciliationStateResponse,
} from '@/types/fund-transactions';
import { formatCurrency, formatDate } from '@/utils/formatters';

import { Field } from './transactionDetails';
import { TransactionDetailsCard } from './TransactionDetailsCard';

export function FundCostDetailsCard({
  transaction,
  fundId,
  fundName,
  reconciliation,
  onViewJournalEntries,
}: {
  transaction: FundTransactionDetail;
  fundId: string;
  fundName: string;
  reconciliation: ReconciliationStateResponse | undefined;
  onViewJournalEntries?: () => void;
}) {
  const snapshot = transaction.fund_cost;

  return (
    <div className="space-y-6">
      <TransactionDetailsCard
        transaction={transaction}
        fundName={fundName}
        reconciliation={reconciliation}
        onViewJournalEntries={onViewJournalEntries}
        icon={<Wallet className="h-5 w-5" />}
        iconClassName="bg-sky-100 text-sky-600 dark:bg-sky-950/40 dark:text-sky-300"
        eyebrow={FUND_TRANSACTION_TYPE_LABELS[transaction.transaction_type]}
        fields={
          <>
            <Field label="Amount">
              {formatCurrency(transaction.amount, {
                currency: transaction.currency,
              })}
            </Field>
            {snapshot && (
              <Field label="Recognised over">
                {`${formatDate(snapshot.period_start)} – ${formatDate(snapshot.period_end)}`}
              </Field>
            )}
            {snapshot && (
              <Field label="Category">
                {
                  FUND_COST_CATEGORY_LABELS[
                    snapshot.cost_category as keyof typeof FUND_COST_CATEGORY_LABELS
                  ]
                }
              </Field>
            )}
          </>
        }
      />
      {snapshot && (
        <RecognitionSchedule
          fundId={fundId}
          costId={snapshot.fund_cost_id}
          currency={transaction.currency}
        />
      )}
    </div>
  );
}

function RecognitionSchedule({
  fundId,
  costId,
  currency,
}: {
  fundId: string;
  costId: string;
  currency: string;
}) {
  const query = useFundCostRecognition(fundId, costId);

  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold">Recognition schedule</h3>
      <DataLoader<FundCostRecognitionEntry[]> query={query}>
        {(schedule) =>
          schedule.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No recognition schedule yet.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Month</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {schedule.map((entry) => (
                  <TableRow key={entry.recognition_month}>
                    <TableCell>{formatDate(entry.recognition_month)}</TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(entry.amount, { currency })}
                    </TableCell>
                    <TableCell>
                      {entry.fund_transaction_id ? (
                        <Link
                          to={`/app/funds/${fundId}/fund-transactions/${entry.fund_transaction_id}`}
                          className="underline-offset-4 hover:underline"
                        >
                          <Badge variant="secondary">{entry.status}</Badge>
                        </Link>
                      ) : (
                        <Badge variant="outline">Not yet due</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )
        }
      </DataLoader>
    </section>
  );
}
